import React, { useCallback, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Trans, useTranslation } from 'react-i18next'
import { FaCaretDown, FaCheck, FaLandmark } from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatCurrency, getApiErrorMessage } from '@utils'
import { ButtonGroup, ButtonProps } from '@components/Button'
import { Checkbox } from '@components/Inputs'
import { DropdownMenu } from '@components/DropdownMenu'
import { Prompt } from '@components/Prompt'
import {
  acceptSaltedgeTos,
  fetchCurrentUser,
  fetchPISPaymentShortcuts,
  IPISPaymentShortcut,
  useCurrentUser
} from '@query'
import { PISProviderSelector } from './PISProviderSelector'
import {
  ButtonContent,
  DropdownButton,
  ImageIcon,
  MenuButton,
  Shimmer,
  ShortcutLabel,
  ShortcutLabelLine,
  StyledButton,
  StyledHolviSvg
} from './PaymentButton.styled'
import { useTheme } from '@root/hooks'
import toast from 'react-hot-toast'

export type PaymentMethod = 'holvi' | 'pis'

interface PaymentMethodOption {
  id?: string
  method: PaymentMethod
  onClick?: () => void | Promise<void>
  disabled?: boolean
  providerName?: string
  logoUrl?: string
  isShortcut?: boolean
}

interface Props extends Omit<ButtonProps, 'onClick' | 'children'> {
  amount?: number
  currency?: string
  showAmount?: boolean
  methods?: PaymentMethodOption[]
  defaultMethod?: PaymentMethod
  businessSlug?: string
  holviEnabled?: boolean
  holviConnectionEnabled?: boolean
  onHolviClick?: () => void
  onPISClick?: () => void
  onPISProviderSelect?: (providerCode: string) => boolean | void | Promise<boolean | void>
  onPISShortcutClick?: (shortcut: IPISPaymentShortcut) => void
  pisShortcuts?: IPISPaymentShortcut[]
}

export const PaymentButton: React.FC<Props> = ({
  amount = 0,
  currency,
  showAmount = true,
  methods,
  defaultMethod,
  businessSlug,
  holviEnabled,
  holviConnectionEnabled,
  onHolviClick,
  onPISClick,
  onPISProviderSelect,
  onPISShortcutClick,
  pisShortcuts,
  disabled,
  showSpinner,
  size = 'md',
  ...props
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [isProviderSelectorVisible, setIsProviderSelectorVisible] = useState(false)
  const allowPIS = Boolean(
    methods?.some(method => method.method === 'pis') ||
      onPISClick ||
      onPISProviderSelect ||
      onPISShortcutClick ||
      pisShortcuts?.length
  )
  const { wrapPISAction, prompt: saltedgeTosPrompt } = useSaltedgeTosGuard({
    enabled: allowPIS
  })
  const { shortcuts: resolvedShortcuts, isLoading: isShortcutsLoading } = useResolvedShortcuts({
    businessSlug,
    methods,
    onPISShortcutClick,
    providedShortcuts: pisShortcuts,
    allowPIS
  })
  const resolvedPISClick = onPISProviderSelect
    ? () => setIsProviderSelectorVisible(true)
    : onPISClick

  const resolvedMethods = resolveMethods({
    methods,
    holviEnabled,
    onHolviClick,
    onPISClick: resolvedPISClick,
    onPISShortcutClick,
    pisShortcuts: resolvedShortcuts,
    allowPIS,
    wrapPISAction
  })
  const resolvedMethod = resolvePrimaryMethod(
    defaultMethod,
    resolvedMethods,
    holviConnectionEnabled
  )

  const mainLabel = resolvedMethod
    ? getLabelNode(resolvedMethod, t)
    : t('components.paymentButton.pay')
  const mainIcon = resolvedMethod
    ? getIcon(resolvedMethod.method, {
        logoUrl: resolvedMethod.logoUrl,
        isMain: true
      })
    : newFallbackIcon()
  const isDisabled = disabled || !!resolvedMethod?.disabled
  const showDropdown = resolvedMethods.length > 1 || (!resolvedMethod && resolvedMethods.length > 0)

  const mainLabelKey = isShortcutsLoading ? 'loading' : resolvedMethod?.id ?? 'pay'
  const mainLabelContent = isShortcutsLoading ? (
    <Shimmer />
  ) : typeof mainLabel === 'string' ? (
    <span>{mainLabel}</span>
  ) : (
    mainLabel
  )
  const animatedContent = (
    <AnimatePresence mode="wait" initial={false}>
      <motion.span
        key={mainLabelKey}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
      >
        {mainLabelContent}
      </motion.span>
    </AnimatePresence>
  )
  const mainButton = (
    <StyledButton
      icon={mainIcon}
      disabled={isDisabled}
      showSpinner={showSpinner}
      size={size}
      onClick={resolvedMethod?.onClick}
      {...props}
    >
      <ButtonContent>
        {animatedContent}
        {showAmount && <span className="monetary">{formatCurrency(amount, null, currency)}</span>}
      </ButtonContent>
    </StyledButton>
  )

  let buttonNode: React.ReactElement

  if (!showDropdown) {
    buttonNode = mainButton
  } else if (!resolvedMethod) {
    buttonNode = (
      <DropdownMenu
        items={resolvedMethods.map(method => (
          <MenuButton
            type="button"
            key={method?.id}
            disabled={disabled || method.disabled}
            onClick={method.onClick}
            icon={getIcon(method.method, { logoUrl: method.logoUrl })}
            style={{ gap: `${theme.spacing.sm}rem`, minHeight: '3rem' }}
          >
            {getLabelNode(method, t)}
          </MenuButton>
        ))}
      >
        <StyledButton
          disabled={disabled || showSpinner}
          size={size}
          {...props}
          iconRight={<FaCaretDown aria-hidden="true" focusable="false" />}
        >
          <ButtonContent>
            {animatedContent}
            {showAmount && (
              <span className="monetary">{formatCurrency(amount, null, currency)}</span>
            )}
          </ButtonContent>
        </StyledButton>
      </DropdownMenu>
    )
  } else {
    buttonNode = (
      <ButtonGroup>
        {mainButton}
        <DropdownMenu
          items={resolvedMethods.map(method => (
            <MenuButton
              type="button"
              key={method?.id}
              disabled={disabled || method.disabled}
              onClick={method.onClick}
              icon={getIcon(method.method, { logoUrl: method.logoUrl })}
              style={{ gap: `${theme.spacing.sm}rem`, minHeight: '3rem' }}
            >
              {getLabelNode(method, t)}
            </MenuButton>
          ))}
        >
          <DropdownButton
            type="button"
            disabled={disabled || showSpinner}
            size={size}
            aria-label={t('components.paymentButton.choosePaymentMethod')}
            icon={<FaCaretDown />}
          />
        </DropdownMenu>
      </ButtonGroup>
    )
  }

  return (
    <>
      {buttonNode}
      {saltedgeTosPrompt}
      {onPISProviderSelect && isProviderSelectorVisible && (
        <PISProviderSelector
          isVisible={isProviderSelectorVisible}
          onClose={() => setIsProviderSelectorVisible(false)}
          onSelectProvider={onPISProviderSelect}
        />
      )}
    </>
  )
}

const useResolvedShortcuts = ({
  businessSlug,
  methods,
  onPISShortcutClick,
  providedShortcuts,
  allowPIS
}: {
  businessSlug?: string
  methods?: PaymentMethodOption[]
  onPISShortcutClick?: (shortcut: IPISPaymentShortcut) => void
  providedShortcuts?: IPISPaymentShortcut[]
  allowPIS: boolean
}) => {
  const shouldFetchShortcuts =
    allowPIS && !!businessSlug && !providedShortcuts && !!onPISShortcutClick && !methods?.length
  const { data: fetchedShortcuts = [], isFetching } = useQuery({
    queryKey: [businessSlug, 'pis_shortcuts'],
    queryFn: () => fetchPISPaymentShortcuts({ slug: businessSlug ?? '' }),
    staleTime: 60 * 1000,
    enabled: shouldFetchShortcuts
  })

  if (!allowPIS) {
    return { shortcuts: [], isLoading: false }
  }

  return {
    shortcuts: providedShortcuts ?? fetchedShortcuts,
    isLoading: shouldFetchShortcuts && isFetching
  }
}

const useSaltedgeTosGuard = ({ enabled }: { enabled: boolean }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data: currentUser } = useCurrentUser()
  const [isPromptVisible, setIsPromptVisible] = useState(false)
  const [isAccepted, setIsAccepted] = useState(false)
  const [pendingAction, setPendingAction] = useState<(() => void | Promise<void>) | null>(null)

  const closePrompt = useCallback(() => {
    setIsPromptVisible(false)
    setIsAccepted(false)
    setPendingAction(null)
  }, [])

  const { mutateAsync: acceptSaltedgeTosMutation, isPending: isAcceptingSaltedgeTos } = useMutation(
    {
      mutationFn: () => acceptSaltedgeTos(),
      ...{
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['user'] })
          await queryClient.fetchQuery({
            queryKey: ['user'],
            queryFn: () => fetchCurrentUser()
          })

          setIsPromptVisible(false)
          setIsAccepted(false)

          if (pendingAction) {
            const actionToRun = pendingAction
            setPendingAction(null)
            await actionToRun()
          }
        },
        onError: (error: unknown) => {
          toast.error(getApiErrorMessage(error, t('general.error')))
        }
      }
    }
  )

  const guardAction = useCallback(
    async (action?: () => void | Promise<void>) => {
      if (!action) return
      if (!enabled) {
        await action()
        return
      }

      const user =
        currentUser ??
        (await queryClient.fetchQuery({
          queryKey: ['user'],
          queryFn: () => fetchCurrentUser()
        }))

      if (user?.saltedge_tos_accepted_at) {
        await action()
        return
      }

      setPendingAction(() => action)
      setIsAccepted(false)
      setIsPromptVisible(true)
    },
    [currentUser, enabled, queryClient]
  )

  const wrapPISAction = useCallback(
    (action?: () => void | Promise<void>) =>
      action
        ? () => {
            void guardAction(action)
          }
        : undefined,
    [guardAction]
  )

  const prompt = (
    <Prompt
      isVisible={isPromptVisible}
      onClose={closePrompt}
      title={t('components.paymentButton.saltedgeTosPrompt.title')}
      description={
        <div>
          <p>{t('components.paymentButton.saltedgeTosPrompt.description')}</p>
          <Checkbox
            id="payment-button-saltedge-tos-checkbox"
            checked={isAccepted}
            onChange={event => setIsAccepted(event.currentTarget.checked)}
            label={
              <Trans
                i18nKey="components.paymentButton.saltedgeTosPrompt.checkbox"
                components={[
                  <a
                    key="saltedge-terms-link"
                    href="https://www.saltedge.com/legal/end_user_license_terms"
                    target="_blank"
                    rel="noreferrer"
                  />
                ]}
              />
            }
          />
        </div>
      }
      buttons={[
        {
          text: t('components.paymentButton.saltedgeTosPrompt.cancel'),
          intent: 'default',
          action: closePrompt,
          disabled: isAcceptingSaltedgeTos
        },
        {
          text: t('components.paymentButton.saltedgeTosPrompt.acceptAndContinue'),
          intent: 'success',
          icon: <FaCheck />,
          action: async () => {
            await acceptSaltedgeTosMutation()
          },
          showSpinner: isAcceptingSaltedgeTos,
          disabled: isAcceptingSaltedgeTos || !isAccepted
        }
      ]}
    />
  )

  return { wrapPISAction, prompt }
}

const resolvePrimaryMethod = (
  preferredMethod: PaymentMethod | undefined,
  methods: ResolvedPaymentMethod[],
  holviConnectionEnabled?: boolean
) => {
  if (!methods.length) {
    return undefined
  }

  if (preferredMethod) {
    return methods.find(method => method.method === preferredMethod) ?? methods[0]
  }

  const holviMethod = methods.find(method => method.method === 'holvi')
  const shortcutMethod = methods.find(method => method.isShortcut)
  const hasConnectionInfo = holviConnectionEnabled !== undefined

  if (holviConnectionEnabled && holviMethod) {
    return holviMethod
  }

  if (hasConnectionInfo && !holviConnectionEnabled && shortcutMethod) {
    return shortcutMethod
  }

  if (hasConnectionInfo && !holviConnectionEnabled && !shortcutMethod) {
    return undefined
  }

  if (holviMethod) {
    return holviMethod
  }

  if (shortcutMethod) {
    return shortcutMethod
  }

  return methods[0]
}

const resolveMethods = ({
  methods,
  holviEnabled,
  onHolviClick,
  onPISClick,
  onPISShortcutClick,
  pisShortcuts,
  allowPIS,
  wrapPISAction
}: {
  methods?: PaymentMethodOption[]
  holviEnabled?: boolean
  onHolviClick?: () => void
  onPISClick?: () => void
  onPISShortcutClick?: (shortcut: IPISPaymentShortcut) => void
  pisShortcuts?: IPISPaymentShortcut[]
  allowPIS: boolean
  wrapPISAction: (action?: () => void | Promise<void>) => (() => void) | undefined
}): ResolvedPaymentMethod[] => {
  if (methods?.length) {
    return methods
      .filter(Boolean)
      .filter(method => allowPIS || method.method !== 'pis')
      .map((method, index) => ({
        id: method.id ?? `${method.method}-${index}`,
        isShortcut: method.isShortcut ?? false,
        ...method,
        onClick: method.method === 'pis' ? wrapPISAction(method.onClick) : method.onClick
      }))
  }

  const builtMethods: ResolvedPaymentMethod[] = []

  if (holviEnabled && onHolviClick) {
    builtMethods.push({ id: 'holvi', method: 'holvi', onClick: onHolviClick, isShortcut: false })
  }

  if (allowPIS && onPISClick) {
    builtMethods.push({
      id: 'pis',
      method: 'pis',
      onClick: wrapPISAction(onPISClick),
      isShortcut: false
    })
  }

  if (allowPIS && pisShortcuts?.length && onPISShortcutClick) {
    builtMethods.push(
      ...pisShortcuts.map(shortcut => ({
        id: `pis-shortcut-${shortcut.id}`,
        method: 'pis' as const,
        onClick: wrapPISAction(() => onPISShortcutClick(shortcut)),
        providerName: shortcut.provider_name ?? shortcut.provider_code,
        logoUrl: shortcut.provider_logo_url ?? undefined,
        isShortcut: true
      }))
    )
  }

  return builtMethods
}

const getLabel = (
  method: PaymentMethod,
  t: (key: string, options?: Record<string, string>) => string
) => {
  switch (method) {
    case 'holvi':
      return t('components.paymentButton.holvi')
    case 'pis':
      return t('components.paymentButton.pis')
    default:
      return t('components.paymentButton.pay')
  }
}

const getLabelNode = (
  method: ResolvedPaymentMethod,
  t: (key: string, options?: Record<string, string>) => string
) => {
  if (!method.isShortcut || !method.providerName) {
    return getLabel(method.method, t)
  }

  return (
    <ShortcutLabel>
      <ShortcutLabelLine $size="xs" $opacity={0.7}>
        {getLabel('pis', t)}
      </ShortcutLabelLine>
      <ShortcutLabelLine $size="xs">{method.providerName}</ShortcutLabelLine>
    </ShortcutLabel>
  )
}

const getIcon = (
  method: PaymentMethod,
  options?: {
    logoUrl?: string
    isMain?: boolean
  }
) => {
  if (method === 'holvi') {
    return <HolviLogo variant={options?.isMain ? 'light' : 'dark'} />
  }

  if (method === 'pis' && options?.logoUrl) {
    return <ImageIcon src={options.logoUrl} alt="" aria-hidden="true" $isMain={options?.isMain} />
  }

  return newFallbackIcon()
}

const newFallbackIcon = () => <FaLandmark />

interface ResolvedPaymentMethod extends PaymentMethodOption {
  id: string
}

const HolviLogo: React.FC<{ variant: 'light' | 'dark' }> = ({ variant }) => (
  <StyledHolviSvg
    viewBox="0 0 23 23"
    aria-hidden="true"
    focusable="false"
    role="presentation"
    $variant={variant}
  >
    <g transform="matrix(1,0,0,1,-24,0)">
      <path
        className="holvi-fill"
        d="M39.64,6.788L41.848,10.648L41.086,15.041L37.708,17.902L33.305,17.902L29.927,15.041L29.165,10.648L31.373,6.788L35.513,5.258L39.64,6.788ZM24.039,10.648L25.117,16.811C25.222,17.423 25.538,17.969 26.011,18.368L30.742,22.388C31.215,22.787 31.807,23 32.411,23L38.588,23C39.206,23 39.798,22.787 40.258,22.388L44.989,18.368C45.462,17.969 45.778,17.423 45.883,16.811L46.96,10.648C47.066,10.036 46.96,9.41 46.658,8.878L43.57,3.461C43.267,2.928 42.781,2.516 42.203,2.303L36.394,0.16C35.815,-0.053 35.184,-0.053 34.619,0.16L28.81,2.303C28.232,2.516 27.759,2.928 27.443,3.461L24.355,8.878C24.039,9.41 23.934,10.036 24.039,10.648Z"
      />
    </g>
  </StyledHolviSvg>
)
